import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowPathIcon, ArrowUturnLeftIcon, CheckIcon, NoSymbolIcon, PlusIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Drawer } from '../ui/Drawer';
import { SelectField, TextAreaField, deletedNoun, isDeletedFlag, normalizeOptions } from './fields';
import MultiSelect from '../MultiSelect';
import EditHistoryLink from './EditHistoryLink';
import LookupSearchField from './LookupSearchField';
import LookupResultCard from '../../pages/order-management/components/LookupResultCard';
import type { CustomDrawerProps } from './TablemaintPage';
import { EQUIPMENT_SIZES, EQUIPMENT_TYPES } from '../../constants/orderFieldOptions';
import quoteService from '../../services/quote.service';
import { getBrokers, type LookupOption } from '../../services/orderLookups.service';
import tablemaintService, { PerdiemRecalcResult, TablemaintRow } from '../../services/tablemaint.service';
import { ScacOption, getScacs } from '../../services/scacs.service';
import type { BillToLookupRow, CustomerLookupRow } from '../../pages/order-management/fixtures';
import ModalHeader from '../ui/ModalHeader';
import { useModalDrag } from '../../hooks/useModalDrag';

/**
 * Bespoke drawer for Customer Per Diem Rules (cms.customerperdiem) — the generic
 * replacement for V1's per-customer per-diem hardcodes. Both directions are the
 * request-form GRID, one row per SCAC (at most ONE line per SCAC for a Bill To/
 * Customer scope — the backend dup-guard enforces the same):
 *
 *  - CREATE: pick the Bill To/Customer once, enter N SCAC rows, save all at once.
 *  - EDIT (click any rule in the results): loads the WHOLE scope's rules in SCAC
 *    order for group editing — change rows, add rows, trash rows — one Save
 *    applies the updates/creates/deletes. Per-row clock icon = edit history.
 *    The scope's DELETED rules load too, grayed below the active rows, so past
 *    setups stay visible and any of them can be restored with the undo arrow.
 *
 * Notes are group-level commentary (stamped on creates; updated across the
 * scope's rows when changed).
 */
const TABLE = 'customer-perdiem';

const s = (v: any): string => (v == null ? '' : String(v));
/** Normalize a record date (ISO date/datetime or MM/DD/YYYY) to YYYY-MM-DD. */
const toIso = (v: any): string => {
  const str = s(v).trim();
  const iso = /^(\d{4}-\d{2}-\d{2})/.exec(str);
  if (iso) return iso[1];
  const mdy = /^(\d{2})\/(\d{2})\/(\d{4})/.exec(str);
  return mdy ? `${mdy[3]}-${mdy[1]}-${mdy[2]}` : '';
};

const isoFromDate = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const mdyFromIso = (iso: string): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[2]}/${m[3]}/${m[1]}` : '';
};

/** 360-style smart date entry (same rules as DateTimePicker's typed input):
 *  "180" = 180 days from now (up to ±999), "1231"/"12/31" = that date this year
 *  (Dec/Jan boundary aware), "5/9/27" or "050927" = m/d/yy, "05092027" or
 *  "05/09/2027" = full. Returns ISO YYYY-MM-DD, or '' if unparseable. */
const parseSmartDate = (raw: string): string => {
  const t = raw.trim();
  if (!t) return '';
  const now = new Date();
  const valid = (y: number, m: number, d: number): string => {
    const date = new Date(y, m - 1, d);
    return date.getMonth() === m - 1 && date.getDate() === d ? isoFromDate(date) : '';
  };
  // Dec/Jan boundary: a yearless date near the year rollover means the adjacent year.
  const boundaryYear = (m: number): number => {
    const cur = now.getMonth();
    let y = now.getFullYear();
    if (cur === 0 && m === 12) y -= 1;
    else if (cur === 11 && m === 1) y += 1;
    return y;
  };
  if (/^-?\d{1,3}$/.test(t)) {
    const d = new Date();
    d.setDate(d.getDate() + parseInt(t, 10));
    return isoFromDate(d);
  }
  if (/^\d{4}$/.test(t)) {
    const m = parseInt(t.slice(0, 2), 10);
    return valid(boundaryYear(m), m, parseInt(t.slice(2, 4), 10));
  }
  if (/^\d{6}$/.test(t)) {
    return valid(2000 + parseInt(t.slice(4, 6), 10), parseInt(t.slice(0, 2), 10), parseInt(t.slice(2, 4), 10));
  }
  if (/^\d{8}$/.test(t)) {
    return valid(parseInt(t.slice(4, 8), 10), parseInt(t.slice(0, 2), 10), parseInt(t.slice(2, 4), 10));
  }
  if (t.includes('/') || t.includes('-')) {
    const p = t.replace(/-/g, '/').split('/');
    if (p.length >= 2) {
      const m = parseInt(p[0], 10);
      const d = parseInt(p[1], 10);
      let y = p[2] ? parseInt(p[2], 10) : boundaryYear(m);
      if (y < 100) y += 2000;
      if (!Number.isNaN(m) && !Number.isNaN(d)) return valid(y, m, d);
    }
  }
  return '';
};

/** Compact grid date cell: free-typed shortcuts parsed on blur/Enter/Tab and
 *  echoed back as MM/DD/YYYY; unparseable text reverts to the last good value. */
const SmartDateCell: React.FC<{
  value: string; // ISO or ''
  onCommit: (iso: string) => void;
  disabled?: boolean;
  className: string;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  id?: string;
}> = ({ value, onCommit, disabled, className, onKeyDown, id }) => {
  const [text, setText] = useState(mdyFromIso(value));
  useEffect(() => { setText(mdyFromIso(value)); }, [value]);
  const commit = () => {
    if (!text.trim()) { if (value) onCommit(''); return; }
    const iso = parseSmartDate(text);
    if (iso) { onCommit(iso); setText(mdyFromIso(iso)); }
    else setText(mdyFromIso(value));
  };
  return (
    <input type="text" id={id} value={text} disabled={disabled} placeholder="MM/DD/YYYY"
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(); } else onKeyDown?.(e); }}
      className={className} />
  );
};

const MOVETYPE_OPTS = ['ALL', 'IMPORT', 'EXPORT', 'REPO', 'RAIL TO PORT'].map((v) => ({ value: v, label: v }));
const MODE_OPTS = ['ALL', 'AWR', 'DIV', 'MLB', 'TRUCK'].map((v) => ({ value: v, label: v }));
// '' = this rule doesn't set free days (amounts only) — an explicit list choice
// so the select stays non-clearable like the grid's other dropdowns (no ✕).
const DAYTYPE_OPTS = [
  { value: '', label: '— Not set (amounts only) —' },
  { value: 'CALENDAR', label: 'Calendar' },
  { value: 'BUSINESS', label: 'Business' },
  { value: 'UNLIMITED', label: 'Unlimited' },
];

// "Update Open Orders" preview/result display: field labels + value formatting
// for the six per diem fields the recalculate writes.
const PD_LABELS: Record<string, string> = {
  perdiemstartdate: 'Per Diem LFD', // not "Last Free Day" — reads as demurrage LFD
  perdiemcalcdate: 'Calc Date',
  perdiemdaysfree: 'Free Days',
  perdiemdaystype: 'Days Type',
  perdiemdayssource: 'Source',
  perdiemcontractnumber: 'Contract #',
};
const fmtPdVal = (field: string, v: string): string => {
  if (!v) return '—';
  return field === 'perdiemstartdate' || field === 'perdiemcalcdate' ? (mdyFromIso(v) || v) : v;
};

// One entry row of the grid / one rule. recno 0 = not saved yet.
// markedDeleted = the row's TARGET state is deleted (trashed now, or already
// deleted in the DB); deletedInDb = what the DB currently says. The gray
// "deleted" section shows markedDeleted rows; Save reconciles the difference
// (trash -> soft-delete, undo on a DB-deleted row -> restore).
interface RuleRow {
  recno: number;
  markedDeleted?: boolean;
  deletedInDb?: boolean;
  /** EXACT orderscurrent match count for this rule (identity-anchored computed
   *  column, comes with the list rows). */
  matchingorders?: number;
  scac: string;
  movetype: string;
  mode: string;
  sizes: string[];
  equiptypes: string[];
  freedays: string;
  freedaystype: string;
  t1amount: string;
  t2days: string;
  t2amount: string;
  t3days: string;
  t3amount: string;
  expirationdate: string; // YYYY-MM-DD
  contractnumber: string;
}

const emptyRow = (): RuleRow => ({
  recno: 0, scac: 'ALL', movetype: 'ALL', mode: 'ALL', sizes: [], equiptypes: [],
  freedays: '', freedaystype: '', t1amount: '', t2days: '', t2amount: '',
  t3days: '', t3amount: '', expirationdate: '', contractnumber: '',
});

const rowFromRecord = (rec: TablemaintRow): RuleRow => ({
  recno: Number(rec.recno),
  markedDeleted: s(rec.deleted) === 'Y',
  deletedInDb: s(rec.deleted) === 'Y',
  matchingorders: rec.matchingorders == null ? undefined : Number(rec.matchingorders),
  scac: s(rec.scac) || 'ALL',
  movetype: s(rec.movetype) || 'ALL',
  mode: s(rec.mode) || 'ALL',
  sizes: Array.isArray(rec.sizes) ? (rec.sizes as string[]) : [],
  equiptypes: Array.isArray(rec.equiptypes) ? (rec.equiptypes as string[]) : [],
  freedays: s(rec.freedays),
  freedaystype: s(rec.freedaystype),
  t1amount: s(rec.t1amount),
  t2days: s(rec.t2days),
  t2amount: s(rec.t2amount),
  t3days: s(rec.t3days),
  t3amount: s(rec.t3amount),
  expirationdate: toIso(rec.expirationdate),
  contractnumber: s(rec.contractnumber),
});

const NUM_KEYS = ['freedays', 't1amount', 't2days', 't2amount', 't3days', 't3amount'] as const;
const TEXT_KEYS = ['scac', 'movetype', 'mode', 'freedaystype', 'contractnumber'] as const;

/** Row validation (mirrors the backend validate rules), keyed to the offending
 *  fields so the grid can highlight the exact cells and show the messages on
 *  the row instead of a distant banner. */
interface RowIssues { fields: Set<string>; messages: string[] }
const rowIssues = (r: RuleRow): RowIssues => {
  const fields = new Set<string>();
  const messages: string[] = [];
  const bad = (f: string | string[], m: string) => {
    (Array.isArray(f) ? f : [f]).forEach((k) => fields.add(k));
    messages.push(m);
  };
  const n = (v: string) => parseFloat(s(v).trim()) || 0;
  const [t1, t2d, t2a, t3d, t3a] = [r.t1amount, r.t2days, r.t2amount, r.t3days, r.t3amount].map(n);
  if (!s(r.scac).trim()) bad('scac', 'SCAC is required.');
  if ((r.freedaystype === 'CALENDAR' || r.freedaystype === 'BUSINESS') && n(r.freedays) <= 0)
    bad('freedays', 'Days is required when a Days Type is set.');
  if (!r.freedaystype && n(r.freedays) > 0) bad('freedaystype', 'Days Type is required when Days is set.');
  if ((t2d || t2a || t3d || t3a) && !t1) bad('t1amount', 'Amt (tier 1 amount/day) is required when tier 2/3 is set.');
  if (!!t2d !== !!t2a) bad(['t2days', 't2amount'], 'T2 Start and T2 Amount must be set together.');
  if (!!t3d !== !!t3a) bad(['t3days', 't3amount'], 'T3 Start and T3 Amount must be set together.');
  if (t3d && !t2d) bad(['t2days', 't3days'], 'Tier 3 requires Tier 2.');
  if (t2d && t3d && t3d <= t2d) bad('t3days', 'T3 Start must be greater than T2 Start.');
  if (!r.freedaystype && !t1) bad(['freedays', 'freedaystype', 't1amount'], 'Set free days (Days/Days Type) and/or amounts (Amt).');
  if (!s(r.expirationdate).trim()) bad('expirationdate', 'Exp Date is required.');
  return { fields, messages };
};

/** Full field payload for one rule (numbers: blank -> '0'; UNLIMITED has no count). */
const rowPayload = (r: RuleRow, billto: number, customercode: number, brokercode: number, notes: string): TablemaintRow => ({
  billto: String(billto || 0),
  customercode: String(customercode || 0),
  brokercode: String(brokercode || 0),
  scac: r.scac,
  movetype: r.movetype || 'ALL',
  mode: r.mode || 'ALL',
  sizes: r.sizes,
  equiptypes: r.equiptypes,
  freedays: r.freedaystype === 'UNLIMITED' ? '0' : (s(r.freedays).trim() || '0'),
  freedaystype: r.freedaystype,
  t1amount: s(r.t1amount).trim() || '0',
  t2days: s(r.t2days).trim() || '0',
  t2amount: s(r.t2amount).trim() || '0',
  t3days: s(r.t3days).trim() || '0',
  t3amount: s(r.t3amount).trim() || '0',
  expirationdate: s(r.expirationdate).trim(),
  contractnumber: r.contractnumber,
  notes,
});

/** A picked scope identity: the stored recno/name + the lookup row it came from
 *  (address etc. for the info line under the field; null when seeded from an
 *  existing record, where only recno + name are known). */
interface ScopeValue {
  recno: number; // 0 = unset
  name: string;
  info: { address1?: string; address2?: string; city?: string; state?: string; zip?: string } | null;
  // Pre-formatted detail line for scopes whose lookup returns one string instead
  // of an address object (broker/forwarder — /cms-lookups/brokers `description`).
  descr?: string;
  /** The referenced record is soft-deleted (grid's *deleted companion column). The
   *  typeahead searches ACTIVE records only, so without this a rule scoped to a
   *  since-deleted bill-to/customer/broker looks perfectly healthy in the drawer. */
  deleted?: boolean;
}

const NO_SCOPE: ScopeValue = { recno: 0, name: '', info: null };

const addressLine = (info: ScopeValue['info']): string => {
  if (!info) return '';
  const street = [info.address1, info.address2].filter(Boolean).join(', ');
  const cityline = [info.city, [info.state, info.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  return [street, cityline].filter(Boolean).join(' — ');
};

const CustomerPerdiemEditDrawer: React.FC<CustomDrawerProps> = ({ row, onClose, onSaved }) => {
  const isNew = !row;
  const groupMode = !!row;

  const [scacOpts, setScacOpts] = useState<ScacOption[]>([]);
  const [billto, setBillto] = useState<ScopeValue>(NO_SCOPE);
  const [customer, setCustomer] = useState<ScopeValue>(NO_SCOPE);
  const [broker, setBroker] = useState<ScopeValue>(NO_SCOPE);
  const [notes, setNotes] = useState('');
  const [rows, setRows] = useState<RuleRow[]>([emptyRow()]);
  const [groupLoading, setGroupLoading] = useState(!!row);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedCount, setSavedCount] = useState(0);
  // Group save keeps the drawer OPEN (so the just-saved rules can be sent through
  // "Update Open Orders" without reopening). justSaved drives the saved indicator +
  // Done button; it visually clears the moment the user edits again (via !dirty).
  const [justSaved, setJustSaved] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  // Unsaved-changes guard: any user edit marks the drawer dirty; closing (X /
  // Esc / backdrop / Cancel) then asks for confirmation instead of silently
  // discarding the grid.
  const dirty = useRef(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const {
    panelRef: discardPanelRef,
    panelStyle: discardPanelStyle,
    dragHandleProps: discardDragHandleProps,
  } = useModalDrag(confirmDiscard);
  // Group-edit bookkeeping: full original records by recno (diff targets).
  const originals = useRef<Record<number, TablemaintRow>>({});
  const notesOriginal = useRef('');
  const scopeOriginal = useRef({ billto: 0, customercode: 0, brokercode: 0 });
  const topRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    getScacs().then((opts) => setScacOpts([{ value: 'ALL', label: 'ALL — Any Steamship Line' }, ...opts])).catch(() => {});
  }, []);

  // Edit mode: ONE list call builds the whole scope's rule set — deleted rules
  // included (grayed at the bottom, restorable, history intact). The list rows
  // carry every field the drawer edits (incl. t3/notes/matchingorders), so
  // there are no per-rule GETs — each of those re-ran the orderscurrent count
  // and made big customers slow to open.
  useEffect(() => {
    if (!row) return;
    let alive = true;
    (async () => {
      try {
        const bt = Number(row.billto || 0);
        const cc = Number(row.customercode || 0);
        const bk = Number(row.brokercode || 0);
        scopeOriginal.current = { billto: bt, customercode: cc, brokercode: bk };
        const all = await tablemaintService.list(TABLE, { includeDeleted: true });
        if (!alive) return;
        const siblings = all.filter((r) =>
          Number(r.billto || 0) === bt && Number(r.customercode || 0) === cc
          && Number(r.brokercode || 0) === bk);
        const clicked = siblings.find((r) => Number(r.recno) === Number(row.recno))
          ?? siblings[0] ?? row;
        setBillto({ recno: bt, name: s(clicked.billtoname), info: null, deleted: isDeletedFlag(clicked.billtodeleted) });
        setCustomer({ recno: cc, name: s(clicked.customername), info: null, deleted: isDeletedFlag(clicked.custdeleted) });
        setBroker({ recno: bk, name: s(clicked.brokername), info: null, deleted: isDeletedFlag(clicked.brokerdeleted) });
        // Backfill the scope footers' address details (the rule row only stores
        // recnos) via the same lookups the typeaheads use — non-blocking.
        if (bt > 0) {
          quoteService.lookupBillTo(String(bt)).then((hits) => {
            if (!alive) return;
            const hit = hits.find((h) => Number(h.billto) === bt);
            if (hit) setBillto((prev) => (prev.recno === bt ? { ...prev, info: hit } : prev));
          }).catch(() => {});
        }
        if (cc > 0) {
          quoteService.lookupCustomer(String(cc)).then((hits) => {
            if (!alive) return;
            const hit = hits.find((h) => Number(h.customercode) === cc);
            if (hit) setCustomer((prev) => (prev.recno === cc ? { ...prev, info: hit } : prev));
          }).catch(() => {});
        }
        if (bk > 0) {
          getBrokers().then((list) => {
            if (!alive) return;
            const hit = list.find((o) => Number(o.value) === bk);
            if (hit) setBroker((prev) => (prev.recno === bk ? { ...prev, descr: hit.description } : prev));
          }).catch(() => {});
        }
        setNotes(s(clicked.notes));
        notesOriginal.current = s(clicked.notes);
        const map: Record<number, TablemaintRow> = {};
        siblings.forEach((rc) => { map[Number(rc.recno)] = rc; });
        originals.current = map;
        setRows(siblings.map(rowFromRecord).sort((a, b) => a.scac.localeCompare(b.scac)));
      } catch {
        if (alive) setSaveError('Failed to load the per diem rules for this customer.');
      } finally {
        if (alive) setGroupLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [row]);

  useEffect(() => { if (saveError || showErrors) topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, [saveError, showErrors]);

  const setRow = (i: number, patch: Partial<RuleRow>) => {
    dirty.current = true;
    setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  };
  const addRow = () => {
    dirty.current = true;
    setRows((prev) => [...prev, emptyRow()]);
  };
  const removeRow = (i: number) => {
    const target = rows[i];
    if (!target) return;
    dirty.current = true;
    if (target.recno) {
      // Existing rule: keep it on screen (grayed, below the active rows) so the
      // history stays reachable and the delete can be undone before Save.
      setRow(i, { markedDeleted: true });
    } else {
      setRows((prev) => {
        const next = prev.filter((_, idx) => idx !== i);
        return next.length || groupMode ? next : prev; // create mode keeps >= 1 row
      });
    }
  };

  // Whole-scope on/off toggle. OFF stages a delete for every existing rule
  // (unsaved new rows just drop); ON un-marks everything — including rules
  // already deleted in the DB, which restore on Save. Same staged mechanism as
  // the per-row trash/undo: gray-out, per-row undo arrows, Save applies.
  const disableAll = () => {
    dirty.current = true;
    setRows((prev) => prev.filter((r) => r.recno > 0).map((r) => ({ ...r, markedDeleted: true })));
  };
  const enableAll = () => {
    dirty.current = true;
    setRows((prev) => prev.map((r) => ({ ...r, markedDeleted: false })));
  };

  // Close guard: veto user-initiated closes (X / Esc / backdrop / Cancel) while
  // there are unsaved edits; the confirm overlay then closes for real via onClose.
  const requestClose = useCallback(() => {
    if (dirty.current && !isSaving) setConfirmDiscard(true);
    else onClose();
  }, [isSaving, onClose]);
  const handleBeforeClose = useCallback(() => {
    if (!dirty.current || isSaving) return true;
    setConfirmDiscard(true);
    return false;
  }, [isSaving]);

  // Drop the blank "— None —" entry the shared constants carry: an EMPTY
  // multiselect already means ALL here (the ALL clear-option row clears back to it).
  const sizeOpts = useMemo(() => normalizeOptions(EQUIPMENT_SIZES as unknown as string[]).filter((o) => o.value !== ''), []);
  const typeOpts = useMemo(() => normalizeOptions(EQUIPMENT_TYPES as unknown as string[]).filter((o) => o.value !== ''), []);

  // One line per (SCAC, Move Type, Sizes, Equip Types) for a Bill To/Customer
  // combo (backend dup-guard mirrors this) — movetype/size/equip splits on one
  // SCAC are legitimate (e.g. different import vs export days). Every ACTIVE
  // row whose full combo repeats is flagged red inline. Rows marked deleted
  // don't count — delete + re-add is legitimate.
  const dupKey = (r: RuleRow) =>
    [s(r.scac).trim().toUpperCase(), s(r.movetype).trim().toUpperCase(),
     [...r.sizes].sort().join('~'), [...r.equiptypes].sort().join('~')].join('|');
  const dupScacRows = useMemo(() => {
    const counts = new Map<string, number>();
    rows.forEach((r) => {
      if (r.markedDeleted || !s(r.scac).trim()) return;
      const k = dupKey(r);
      counts.set(k, (counts.get(k) ?? 0) + 1);
    });
    return new Set(rows.map((r, i) => i).filter((i) =>
      !rows[i].markedDeleted && s(rows[i].scac).trim()
      && (counts.get(dupKey(rows[i])) ?? 0) > 1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  // Per-row issues render WITH the row (red cells + message line); only
  // drawer-level problems go in the top banner. Deleted-marked rows are
  // read-only and skip validation.
  const rowIssuesList = useMemo(
    () => rows.map((r) => (r.markedDeleted ? { fields: new Set<string>(), messages: [] as string[] } : rowIssues(r))),
    [rows]);
  const globalErrors = useMemo(() => {
    const e: string[] = [];
    if (!billto.recno && !customer.recno && !broker.recno) e.push('Scope the rules to a Bill To, Customer, and/or Broker.');
    if (isNew && rows.length === 0) e.push('Add at least one rule row.');
    return e;
  }, [billto.recno, customer.recno, broker.recno, isNew, rows.length]);
  const hasErrors = globalErrors.length > 0 || dupScacRows.size > 0
    || rowIssuesList.some((x) => x.messages.length > 0);
  const banner = [...(showErrors ? globalErrors : []), ...(saveError ? [saveError] : [])];

  /** Diff one loaded row against its original record (group/restore save). */
  const patchForRow = useCallback((r: RuleRow): TablemaintRow => {
    const orig = originals.current[r.recno] || {};
    const full = rowPayload(r, billto.recno, customer.recno, broker.recno, notes);
    const patch: TablemaintRow = {};
    if (Number(orig.billto || 0) !== billto.recno) patch.billto = full.billto;
    if (Number(orig.customercode || 0) !== customer.recno) patch.customercode = full.customercode;
    if (Number(orig.brokercode || 0) !== broker.recno) patch.brokercode = full.brokercode;
    TEXT_KEYS.forEach((k) => { if (s(full[k]) !== s(orig[k])) patch[k] = full[k]; });
    NUM_KEYS.forEach((k) => { if (s(r[k]).trim() !== s(orig[k]).trim()) patch[k] = full[k]; });
    if (s(r.expirationdate).trim() !== toIso(orig.expirationdate)) patch.expirationdate = full.expirationdate;
    if (s(notes) !== notesOriginal.current) patch.notes = notes;
    const origSizes = Array.isArray(orig.sizes) ? (orig.sizes as string[]) : [];
    const origTypes = Array.isArray(orig.equiptypes) ? (orig.equiptypes as string[]) : [];
    if (r.sizes.join('~') !== origSizes.join('~')) patch.sizes = r.sizes;
    if (r.equiptypes.join('~') !== origTypes.join('~')) patch.equiptypes = r.equiptypes;
    return patch;
  }, [billto.recno, customer.recno, broker.recno, notes]);

  const handleSave = useCallback(async () => {
    if (hasErrors) { setShowErrors(true); return; }
    setShowErrors(false);
    setSaveError(null);
    setIsSaving(true);
    try {
      if (isNew) {
        // Create each grid row; on a failure keep the unsaved rows so it can be
        // fixed and retried without re-entering everything.
        let created = 0;
        try {
          while (created < rows.length) {
            await tablemaintService.create(TABLE, rowPayload(rows[created], billto.recno, customer.recno, broker.recno, notes));
            created += 1;
          }
        } catch (err: any) {
          setRows(rows.slice(created));
          setSavedCount((n) => n + created);
          if (created > 0) onSaved();
          const detail = err?.response?.data?.detail || err?.message || 'Failed to save per diem rule.';
          setSaveError(`Row ${created + 1}${rows.length > 1 ? ` of ${rows.length}` : ''}: ${detail}`);
          return;
        }
        setSavedCount((n) => n + created);
        dirty.current = false;
        onSaved();
        onClose();
      } else {
        // Group save: deletes -> updates/restores -> creates, sequential so a
        // failure points at the exact operation; already-applied ops stand.
        let touched = 0;
        try {
          for (const r of rows) {
            if (r.markedDeleted && !r.deletedInDb && r.recno) {
              await tablemaintService.update(TABLE, r.recno, { deleted: 'Y' });
              touched += 1;
            }
          }
          for (let i = 0; i < rows.length; i += 1) {
            const r = rows[i];
            if (r.markedDeleted) continue; // target state deleted (incl. already-deleted rows left as-is)
            if (r.recno) {
              const patch = patchForRow(r);
              if (r.deletedInDb) patch.deleted = 'N'; // undo on a DB-deleted row = restore
              if (Object.keys(patch).length > 0) {
                await tablemaintService.update(TABLE, r.recno, patch);
                touched += 1;
              }
            } else {
              const res = await tablemaintService.create(TABLE, rowPayload(r, billto.recno, customer.recno, broker.recno, notes));
              setRow(i, { recno: Number(res.recno) });
              touched += 1;
            }
          }
        } catch (err: any) {
          if (touched > 0) onSaved();
          const detail = err?.response?.data?.detail || err?.message || 'Failed to save per diem rules.';
          setSaveError(detail);
          return;
        }
        // Keep the drawer OPEN so the just-saved scope can be sent through "Update
        // Open Orders" without reopening. dirty clears (re-enabling that button);
        // justSaved shows the saved state + flips Cancel → Done.
        dirty.current = false;
        setJustSaved(true);
        onSaved();
      }
    } catch (err: any) {
      setSaveError(err?.response?.data?.detail || err?.message || 'Failed to save per diem rules.');
    } finally {
      setIsSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasErrors, isNew, rows, billto.recno, customer.recno, broker.recno, notes, patchForRow, onClose, onSaved]);

  // "Update Open Orders" — the deliberate reapply path. The button ALWAYS
  // opens a preview first (which pros, each field old -> new, nothing written);
  // only the Process button inside the modal writes. Runs against SAVED rules,
  // so unsaved drawer edits ask for a Save first instead of previewing stale.
  const [recalc, setRecalc] = useState<{
    open: boolean; loading: boolean; applying: boolean;
    result: PerdiemRecalcResult | null; error: string | null;
  }>({ open: false, loading: false, applying: false, result: null, error: null });
  const [recalcTab, setRecalcTab] = useState<'change' | 'match' | 'skipped'>('change');
  const {
    panelRef: recalcPanelRef,
    panelStyle: recalcPanelStyle,
    dragHandleProps: recalcDragHandleProps,
  } = useModalDrag(recalc.open);
  const closeRecalc = () => setRecalc({ open: false, loading: false, applying: false, result: null, error: null });
  const openRecalc = async () => {
    if (dirty.current) {
      setRecalc({ open: true, loading: false, applying: false, result: null,
        error: 'Save your changes first — the preview runs against the saved rules.' });
      return;
    }
    setRecalcTab('change');
    setRecalc({ open: true, loading: true, applying: false, result: null, error: null });
    try {
      const result = await tablemaintService.perdiemRecalculate({ billto: billto.recno, customercode: customer.recno, brokercode: broker.recno, apply: false });
      setRecalc({ open: true, loading: false, applying: false, result, error: null });
    } catch (err: any) {
      setRecalc({ open: true, loading: false, applying: false, result: null,
        error: err?.response?.data?.detail || err?.message || 'Failed to compute the preview.' });
    }
  };
  const confirmRecalc = async () => {
    setRecalc((p) => ({ ...p, applying: true, error: null }));
    try {
      const result = await tablemaintService.perdiemRecalculate({ billto: billto.recno, customercode: customer.recno, brokercode: broker.recno, apply: true });
      setRecalc({ open: true, loading: false, applying: false, result, error: null });
    } catch (err: any) {
      setRecalc((p) => ({ ...p, applying: false,
        error: err?.response?.data?.detail || err?.message || 'Processing failed — no orders were changed.' }));
    }
  };

  const btnSecondary = 'px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50';
  const footer = (
    <div className="flex items-center justify-between gap-3">
      <div>
        {groupMode && (
          <div className="flex items-center gap-2">
            <button onClick={openRecalc} disabled={isSaving || groupLoading || dirty.current}
              title={dirty.current
                ? 'Save your changes first — the preview runs against the saved rules'
                : 'Preview which open orders these rules would change (old → new per pro), then confirm to process'}
              className={`${btnSecondary} inline-flex items-center gap-2`}>
              <ArrowPathIcon className="w-4 h-4" /> Update Open Orders…
            </button>
            {dirty.current && (
              <span className="text-xs text-amber-600 dark:text-amber-400">Save changes first</span>
            )}
          </div>
        )}
      </div>
      <div className="flex items-center gap-3">
        {savedCount > 0 && <span className="text-sm text-green-700 dark:text-green-400">{savedCount} rule{savedCount > 1 ? 's' : ''} added</span>}
        {justSaved && !dirty.current && (
          <span className="text-sm text-green-700 dark:text-green-400 inline-flex items-center gap-1">
            <CheckIcon className="w-4 h-4" /> Saved
          </span>
        )}
        <button onClick={requestClose} disabled={isSaving} className={btnSecondary}>
          {(!dirty.current && (savedCount > 0 || justSaved)) ? 'Done' : 'Cancel'}
        </button>
        <button onClick={handleSave} disabled={isSaving || groupLoading}
          className="px-4 py-2 text-sm font-medium text-accent-fg bg-accent rounded-lg hover:bg-accent-hover disabled:opacity-50 inline-flex items-center gap-2">
          {isSaving ? (<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving...</>)
            : (<><CheckIcon className="w-4 h-4" /> {isNew ? `Create ${rows.length > 1 ? `${rows.length} Rules` : 'Rule'}` : 'Save Changes'}</>)}
        </button>
      </div>
    </div>
  );

  const scopeName = billto.name || customer.name;
  const title = isNew ? 'Add Per Diem Rules' : `Per Diem Rules — ${scopeName || 'Loading…'}`;
  const numInput = 'w-full px-1.5 py-1 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white text-sm';

  // Up/Down in a grid cell moves to the same field one row up/down (instead of
  // spinning a number value — the cells are text inputs with numeric filtering).
  const cellId = (k: string, i: number) => `pdgrid-${k}-${i}`;
  const rowNavKeyDown = (k: string, i: number) => (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
    e.preventDefault();
    const target = document.getElementById(cellId(k, e.key === 'ArrowUp' ? i - 1 : i + 1)) as HTMLInputElement | null;
    if (target) { target.focus(); target.select?.(); }
  };
  // Red-flag a cell once save was attempted and the field is part of a row issue.
  const flagCell = (i: number, k: string): boolean => showErrors && (rowIssuesList[i]?.fields.has(k) ?? false);
  const flagRing = 'ring-2 ring-red-400 border-red-400';
  const NumCell = (i: number, k: typeof NUM_KEYS[number], opts?: { disabled?: boolean }) => (
    <input type="text" inputMode="decimal" id={cellId(k, i)} value={s(rows[i]?.[k])} disabled={isSaving || opts?.disabled}
      onChange={(e) => { const v = e.target.value; if (/^\d*\.?\d*$/.test(v)) setRow(i, { [k]: v } as Partial<RuleRow>); }}
      onKeyDown={rowNavKeyDown(k, i)}
      className={`${numInput} ${opts?.disabled ? 'opacity-50' : ''} ${flagCell(i, k) ? flagRing : ''}`} />
  );

  // Identical footer under both scope fields: #recno + address (when known) + clear ×.
  const scopeFooter = (label: string, value: ScopeValue, onClear: () => void) => (
    <div className="mt-1.5 flex items-center gap-2 text-xs">
      {value.recno > 0 ? (
        <>
          <span className={`font-medium ${value.deleted ? 'text-red-600 dark:text-red-400' : 'text-green-700 dark:text-green-400'}`}>
            #{value.recno}
          </span>
          {value.deleted && (
            <span className="font-semibold text-red-600 dark:text-red-400">
              This {deletedNoun(label)} has been deleted.
            </span>
          )}
          {(addressLine(value.info) || value.descr) && (
            <span className="text-muted truncate">{addressLine(value.info) || value.descr}</span>
          )}
          {!isSaving && (
            <button type="button" onClick={onClear} title={`Clear ${label}`}
              className="text-gray-400 hover:text-red-500">
              <XMarkIcon className="w-3.5 h-3.5" />
            </button>
          )}
        </>
      ) : (
        <span className="text-muted">No {label} selected</span>
      )}
    </div>
  );

  const scopeSection = (
    <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
      <legend className="px-2 text-sm font-semibold text-label">Customer Scope</legend>
      <p className="text-xs text-muted mb-3">Bill To, Customer, and/or Broker is required — every rule below belongs to this scope. Setting more than one narrows the match (all that are set must be on the order).</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <LookupSearchField<BillToLookupRow>
            label="Bill To"
            displayValue={billto.name}
            onType={(text) => { dirty.current = true; setBillto({ recno: 0, name: text, info: null }); }}
            onSelect={(r) => { dirty.current = true; setBillto({ recno: r.billto, name: r.billtoname, info: r }); }}
            search={(q) => quoteService.lookupBillTo(q)}
            renderItem={(r) => (
              <LookupResultCard name={r.billtoname} code={r.billto}
                address1={r.address1} address2={r.address2} city={r.city} state={r.state} zip={r.zip}
                details={[
                  { label: 'SAGE', value: r.mas90code },
                  { label: 'Terms', value: r.creditTerms },
                  { label: 'Credit Hold', value: r.creditHold === 'Y' ? 'Yes' : '', danger: true },
                ]} />
            )}
            itemValue={(r) => String(r.billto)}
            itemLabel={(r) => r.billtoname}
            disabled={isSaving || groupLoading}
            placeholder="Search Bill Tos by name or #…"
          />
          {scopeFooter('Bill To', billto, () => { dirty.current = true; setBillto(NO_SCOPE); })}
        </div>
        <div>
          <LookupSearchField<CustomerLookupRow>
            label="Customer"
            displayValue={customer.name}
            onType={(text) => { dirty.current = true; setCustomer({ recno: 0, name: text, info: null }); }}
            onSelect={(r) => { dirty.current = true; setCustomer({ recno: r.customercode, name: r.custname, info: r }); }}
            search={(q) => quoteService.lookupCustomer(q)}
            renderItem={(r) => (
              <LookupResultCard name={r.custname} code={r.customercode}
                address1={r.address1} address2={r.address2} city={r.city} state={r.state} zip={r.zip}
                details={[{ label: 'Market', value: r.market }]} />
            )}
            itemValue={(r) => String(r.customercode)}
            itemLabel={(r) => r.custname}
            disabled={isSaving || groupLoading}
            placeholder="Search Customers by name or #…"
          />
          {scopeFooter('Customer', customer, () => { dirty.current = true; setCustomer(NO_SCOPE); })}
        </div>
        <div>
          <LookupSearchField<LookupOption>
            label="Broker / Forwarder"
            displayValue={broker.name}
            onType={(text) => { dirty.current = true; setBroker({ recno: 0, name: text, info: null }); }}
            onSelect={(o) => { dirty.current = true; setBroker({ recno: Number(o.value), name: o.label, info: null, descr: o.description }); }}
            search={(q) => getBrokers().then((list) => {
              const needle = q.trim().toLowerCase();
              return (needle
                ? list.filter((o) => o.label.toLowerCase().includes(needle) || String(o.value).includes(needle))
                : list).slice(0, 50);
            })}
            renderItem={(o) => (<LookupResultCard name={o.label} code={o.value} address1={o.description} />)}
            itemValue={(o) => String(o.value)}
            itemLabel={(o) => o.label}
            disabled={isSaving || groupLoading}
            placeholder="Search Brokers by name or #…"
          />
          {scopeFooter('Broker', broker, () => { dirty.current = true; setBroker(NO_SCOPE); })}
        </div>
      </div>
    </fieldset>
  );

  const th = 'px-1.5 py-1 text-left text-xs font-semibold text-muted whitespace-nowrap';
  // Active rows render first (numbered); rows marked deleted sink below them,
  // grayed + read-only, with history and an undo-delete still available.
  const activeEntries = rows.map((r, i) => ({ r, i })).filter(({ r }) => !r.markedDeleted);
  const deletedEntries = rows.map((r, i) => ({ r, i })).filter(({ r }) => r.markedDeleted);
  const dtd = 'px-2 py-2 text-sm line-through text-muted whitespace-nowrap';
  const rulesGrid = (
    <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
      <legend className="px-2 text-sm font-semibold text-label">Per Diem Rules</legend>
      {groupLoading ? (
        <div className="flex items-center gap-2 py-6 justify-center text-sm text-muted">
          <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /> Loading rules…
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" style={{ minWidth: "72rem" }}>
              <thead>
                <tr>
                  <th className={`${th} w-7`}>#</th>
                  <th className={`${th} min-w-[9rem]`}>SCAC *</th>
                  <th className={`${th} min-w-[8rem]`}>Move Type *</th>
                  <th className={`${th} min-w-[6.5rem]`}>Mode</th>
                  <th className={`${th} min-w-[6rem]`}>Sizes</th>
                  <th className={`${th} min-w-[8rem]`}>Equip Types</th>
                  <th className={`${th} w-14`}>Days</th>
                  <th className={`${th} min-w-[7.5rem]`}>Days Type</th>
                  <th className={`${th} w-[4.5rem]`}>Amt</th>
                  <th className={`${th} w-14`}>T2 Start</th>
                  <th className={`${th} w-[4.5rem]`}>T2 Amt</th>
                  <th className={`${th} w-14`}>T3 Start</th>
                  <th className={`${th} w-[4.5rem]`}>T3 Amt</th>
                  <th className={`${th} min-w-[8rem]`}>Exp Date *</th>
                  <th className={`${th} min-w-[5.5rem]`}>Contract #</th>
                  <th className={`${th} w-14 text-right`}>Orders</th>
                  <th className={`${th} w-16`} />
                </tr>
              </thead>
              <tbody>
                {activeEntries.map(({ r, i }, displayIdx) => (
                  <React.Fragment key={r.recno || `new-${i}`}>
                    <tr className="align-top">
                      <td className="px-1.5 py-1 text-xs text-gray-400">{displayIdx + 1}</td>
                      <td className="px-1 py-1">
                        <div className={dupScacRows.has(i) || flagCell(i, 'scac') ? 'rounded-lg ring-2 ring-red-400' : ''}>
                          <SelectField label="SCAC" hideLabel compact showSearch={false} clearable={false} value={r.scac} onChange={(v) => setRow(i, { scac: v })} options={scacOpts} disabled={isSaving} />
                        </div>
                        {dupScacRows.has(i) && (
                          <p className="mt-0.5 text-[0.6875rem] font-medium text-red-600 dark:text-red-400">Already exists</p>
                        )}
                      </td>
                      <td className="px-1 py-1"><SelectField label="Move Type" hideLabel compact showSearch={false} clearable={false} value={r.movetype} onChange={(v) => setRow(i, { movetype: v })} options={MOVETYPE_OPTS} disabled={isSaving} /></td>
                      <td className="px-1 py-1"><SelectField label="Mode" hideLabel compact showSearch={false} clearable={false} value={r.mode} onChange={(v) => setRow(i, { mode: v })} options={MODE_OPTS} disabled={isSaving} /></td>
                      <td className="px-1 py-1"><MultiSelect options={sizeOpts} value={r.sizes} onChange={(v) => setRow(i, { sizes: v.filter(Boolean) })} disabled={isSaving} usePortal enableSearch size="sm" placeholder="ALL" showClearOption clearOptionLabel="ALL" /></td>
                      <td className="px-1 py-1"><MultiSelect options={typeOpts} value={r.equiptypes} onChange={(v) => setRow(i, { equiptypes: v.filter(Boolean) })} disabled={isSaving} usePortal enableSearch size="sm" placeholder="ALL" showClearOption clearOptionLabel="ALL" /></td>
                      <td className="px-1 py-1">{NumCell(i, 'freedays', { disabled: r.freedaystype === 'UNLIMITED' })}</td>
                      <td className="px-1 py-1">
                        <div className={flagCell(i, 'freedaystype') ? 'rounded-lg ring-2 ring-red-400' : ''}>
                          <SelectField label="Days Type" hideLabel compact showSearch={false} clearable={false} value={r.freedaystype} onChange={(v) => setRow(i, { freedaystype: v })} options={DAYTYPE_OPTS} disabled={isSaving} />
                        </div>
                      </td>
                      <td className="px-1 py-1">{NumCell(i, 't1amount')}</td>
                      <td className="px-1 py-1">{NumCell(i, 't2days')}</td>
                      <td className="px-1 py-1">{NumCell(i, 't2amount')}</td>
                      <td className="px-1 py-1">{NumCell(i, 't3days')}</td>
                      <td className="px-1 py-1">{NumCell(i, 't3amount')}</td>
                      <td className="px-1 py-1">
                        <SmartDateCell id={cellId('expirationdate', i)} value={r.expirationdate} disabled={isSaving}
                          onCommit={(iso) => setRow(i, { expirationdate: iso })}
                          onKeyDown={rowNavKeyDown('expirationdate', i)}
                          className={`${numInput} ${flagCell(i, 'expirationdate') ? flagRing : ''}`} />
                      </td>
                      <td className="px-1 py-1">
                        <input type="text" id={cellId('contractnumber', i)} value={r.contractnumber} disabled={isSaving} maxLength={40}
                          onChange={(e) => setRow(i, { contractnumber: e.target.value })}
                          onKeyDown={rowNavKeyDown('contractnumber', i)} className={numInput} />
                      </td>
                      <td className="px-2 py-2 text-sm text-right text-muted"
                        title="Open orders in orderscurrent matching this rule's full criteria">
                        {r.recno > 0 ? (r.matchingorders ?? '') : ''}
                      </td>
                      <td className="px-1 py-1 whitespace-nowrap">
                        {r.recno > 0 && (
                          <EditHistoryLink table={TABLE} recno={r.recno} compact label={`${r.scac} (#${r.recno})`} />
                        )}
                        {(isNew ? rows.length > 1 : true) && (
                          <button type="button" onClick={() => removeRow(i)} disabled={isSaving}
                            className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                            title={r.recno ? 'Delete this rule (applies on Save)' : 'Remove row'}>
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                    {showErrors && rowIssuesList[i]?.messages.length > 0 && (
                      <tr>
                        <td />
                        <td colSpan={16} className="px-1 pb-2">
                          <p className="text-[0.6875rem] font-medium text-red-600 dark:text-red-400">
                            {rowIssuesList[i].messages.join(' ')}
                          </p>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
                {deletedEntries.length > 0 && (
                  <tr>
                    <td colSpan={17} className="px-1.5 pt-3 pb-1 text-[0.6875rem] font-semibold text-red-600 dark:text-red-400">
                      Deleted rules — the undo arrow restores one (applies on Save).
                    </td>
                  </tr>
                )}
                {deletedEntries.map(({ r, i }) => (
                  <tr key={`del-${r.recno}`} className="align-top bg-gray-50 dark:bg-gray-800/40 opacity-70">
                    <td className="px-1.5 py-2 text-xs font-semibold text-red-500">✕</td>
                    <td className={dtd}>{r.scac}</td>
                    <td className={dtd}>{r.movetype}</td>
                    <td className={dtd}>{r.mode}</td>
                    <td className={dtd}>{r.sizes.join(', ') || 'ALL'}</td>
                    <td className={dtd}>{r.equiptypes.join(', ') || 'ALL'}</td>
                    <td className={dtd}>{r.freedaystype === 'UNLIMITED' ? '' : r.freedays}</td>
                    <td className={dtd}>{r.freedaystype}</td>
                    <td className={dtd}>{r.t1amount}</td>
                    <td className={dtd}>{r.t2days}</td>
                    <td className={dtd}>{r.t2amount}</td>
                    <td className={dtd}>{r.t3days}</td>
                    <td className={dtd}>{r.t3amount}</td>
                    <td className={dtd}>{mdyFromIso(r.expirationdate)}</td>
                    <td className={dtd}>{r.contractnumber}</td>
                    <td className={`${dtd} text-right`}>{r.matchingorders ?? ''}</td>
                    <td className="px-1 py-1 whitespace-nowrap">
                      <EditHistoryLink table={TABLE} recno={r.recno} compact label={`${r.scac} (#${r.recno})`} />
                      <button type="button" disabled={isSaving}
                        onClick={() => { dirty.current = true; setRow(i, { markedDeleted: false }); }}
                        className="p-1.5 text-gray-400 hover:text-green-600 dark:hover:text-green-400"
                        title="Undo delete — keep this rule">
                        <ArrowUturnLeftIcon className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Disable/Enable All sits far right so it can't be fat-fingered from Add Row. */}
          <div className="mt-3 flex items-center justify-between">
            <button type="button" onClick={addRow} disabled={isSaving}
              className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50">
              <PlusIcon className="w-4 h-4" /> Add Row
            </button>
            <div>
              {groupMode && activeEntries.some(({ r }) => r.recno > 0) && (
                <button type="button" onClick={disableAll} disabled={isSaving}
                  title="Turn this Bill To/Customer OFF — every rule marked deleted (applies on Save)"
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-red-600 dark:text-red-400 hover:underline disabled:opacity-50">
                  <NoSymbolIcon className="w-4 h-4" /> Disable All Rules
                </button>
              )}
              {groupMode && activeEntries.every(({ r }) => !(r.recno > 0)) && deletedEntries.length > 0 && (
                <button type="button" onClick={enableAll} disabled={isSaving}
                  title="Turn this Bill To/Customer back ON — every rule restored (applies on Save)"
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-green-700 dark:text-green-400 hover:underline disabled:opacity-50">
                  <ArrowUturnLeftIcon className="w-4 h-4" /> Enable All Rules
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </fieldset>
  );

  return (
    <Drawer isOpen onClose={onClose} onBeforeClose={handleBeforeClose} title={title} position="right" width="calc(100vw - 240px)" footer={footer}>
      <div className="space-y-5">
        <div ref={topRef} />
        {banner.length > 0 && (
          <div className="rounded-lg border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30 p-3">
            <ul className="list-disc list-inside text-sm text-red-800 dark:text-red-300">
              {banner.map((m, i) => <li key={i}>{m}</li>)}
            </ul>
          </div>
        )}

        {scopeSection}
        {rulesGrid}

        <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <legend className="px-2 text-sm font-semibold text-label">Notes</legend>
          <TextAreaField label="Internal comments/notes" value={notes} onChange={(v) => { dirty.current = true; setNotes(v); }} disabled={isSaving || groupLoading} rows={3} />
        </fieldset>

      </div>

      {/* Update Open Orders: preview (old → new per pro) then explicit confirm.
          Nothing is written until "Process" is clicked; the same modal then
          shows exactly what got updated. */}
      {recalc.open && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-gray-900/40 p-6">
          <div
            ref={recalcPanelRef}
            style={recalcPanelStyle}
            className="w-[58rem] max-w-[95vw] max-h-[85vh] flex flex-col rounded-xl border border-gray-200 dark:border-gray-700 bg-panel shadow-xl"
          >
            <ModalHeader
              title={`Update Open Orders — ${scopeName || 'this customer'}`}
              onClose={recalc.applying ? undefined : closeRecalc}
              dragHandleProps={recalcDragHandleProps}
              className="rounded-t-xl"
            />
            {recalc.result && (
              <div className="px-5 pt-3 pb-3">
                <p className="text-sm text-muted">
                  {recalc.result.applied
                    ? `${recalc.result.updates.length} open order${recalc.result.updates.length === 1 ? '' : 's'} updated.`
                    : `${recalc.result.counts.candidates} open order${recalc.result.counts.candidates === 1 ? '' : 's'} in scope — ` +
                      `${recalc.result.updates.length} would change, ${recalc.result.counts.unchanged} already correct` +
                      `${recalc.result.counts.skipped ? `, ${recalc.result.counts.skipped} skipped` : ''}.`}
                </p>
              </div>
            )}

            {recalc.result && !recalc.loading && (
              <div className="px-5 flex items-end gap-1 border-b border-gray-200 dark:border-gray-700">
                {([
                  ['change', `${recalc.result.applied ? 'Changed' : 'Change'} (${recalc.result.updates.length})`],
                  ['match', `Match (${recalc.result.unchanged.length})`],
                  ['skipped', `Skipped (${recalc.result.counts.skipped})`],
                ] as const).map(([key, label]) => (
                  <button key={key} type="button" onClick={() => setRecalcTab(key)}
                    className={`px-3 py-2 text-sm border-b-2 -mb-px ${recalcTab === key
                      ? 'border-blue-600 text-blue-600 dark:text-blue-400 font-medium'
                      : 'border-transparent text-muted hover:text-gray-700 dark:hover:text-gray-200'}`}>
                    {label}
                  </button>
                ))}
              </div>
            )}

            <div className="px-5 py-3 overflow-y-auto flex-1 space-y-4">
              {recalc.loading && (
                <div className="flex items-center gap-2 py-6 text-sm text-muted">
                  <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                  Computing preview — nothing is being changed…
                </div>
              )}
              {recalc.error && (
                <div className="rounded-lg border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30 p-3 text-sm text-red-800 dark:text-red-300">
                  {recalc.error}
                </div>
              )}

              {recalc.result && recalcTab === 'change' && recalc.result.updates.length > 0 && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted mb-1">
                    {recalc.result.applied ? 'Updated' : 'Will update'}
                  </p>
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 dark:bg-gray-700/50">
                        <tr className="text-left text-xs uppercase tracking-wide text-muted">
                          <th className="px-3 py-1.5">Pro</th>
                          <th className="px-3 py-1.5">Container</th>
                          <th className="px-3 py-1.5">SCAC</th>
                          <th className="px-3 py-1.5">Move Type</th>
                          <th className="px-3 py-1.5 w-full">Changes (old → new)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recalc.result.updates.map((u) => (
                          <tr key={u.pronumber} className="border-t border-gray-100 dark:border-gray-700 align-top">
                            <td className="px-3 py-1.5 font-medium text-body">{u.pronumber}</td>
                            <td className="px-3 py-1.5 whitespace-nowrap">{u.container || '—'}</td>
                            <td className="px-3 py-1.5">{u.scac}</td>
                            <td className="px-3 py-1.5 whitespace-nowrap">{u.movetype}</td>
                            <td className="px-3 py-1.5">
                              {Object.entries(u.changes).map(([f, ch]) => (
                                <div key={f} className="whitespace-nowrap">
                                  <span className="text-muted">{PD_LABELS[f] ?? f}:</span>{' '}
                                  <span className="text-muted line-through decoration-red-400/70">{fmtPdVal(f, ch.old)}</span>
                                  {' → '}
                                  <span className="font-medium text-body">{fmtPdVal(f, ch.new)}</span>
                                </div>
                              ))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {recalc.result && recalcTab === 'change' && recalc.result.updates.length === 0 && !recalc.result.applied && !recalc.loading && (
                <p className="text-sm text-muted py-2">
                  No open orders need updating — everything already matches the current rules.
                </p>
              )}

              {recalc.result && recalcTab === 'match' && (
                recalc.result.unchanged.length === 0 ? (
                  <p className="text-sm text-muted py-2">No orders currently match the rules exactly.</p>
                ) : (
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 dark:bg-gray-700/50">
                        <tr className="text-left text-xs uppercase tracking-wide text-muted">
                          <th className="px-3 py-1.5">Pro</th>
                          <th className="px-3 py-1.5">Container</th>
                          <th className="px-3 py-1.5">SCAC</th>
                          <th className="px-3 py-1.5">Move Type</th>
                          <th className="px-3 py-1.5">Per Diem LFD</th>
                          <th className="px-3 py-1.5">Free Days</th>
                          <th className="px-3 py-1.5 w-full">Source</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recalc.result.unchanged.map((m) => (
                          <tr key={m.pronumber} className="border-t border-gray-100 dark:border-gray-700">
                            <td className="px-3 py-1.5 font-medium text-body">{m.pronumber}</td>
                            <td className="px-3 py-1.5 whitespace-nowrap">{m.container || '—'}</td>
                            <td className="px-3 py-1.5">{m.scac}</td>
                            <td className="px-3 py-1.5 whitespace-nowrap">{m.movetype}</td>
                            <td className="px-3 py-1.5 whitespace-nowrap">{fmtPdVal('perdiemstartdate', m.current.perdiemstartdate)}</td>
                            <td className="px-3 py-1.5 whitespace-nowrap">
                              {m.current.perdiemdaysfree || '—'}{m.current.perdiemdaystype ? ` ${m.current.perdiemdaystype}` : ''}
                            </td>
                            <td className="px-3 py-1.5">{m.current.perdiemdayssource || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )
              )}

              {recalc.result && recalcTab === 'skipped' && (
                recalc.result.skipped.length === 0 ? (
                  <p className="text-sm text-muted py-2">Nothing was skipped.</p>
                ) : (
                  <div className="space-y-4">
                    {recalc.result.skipped.map((sk) => (
                      <div key={sk.reason}>
                        <p className="text-sm text-label">
                          <span className="font-semibold">{sk.count}</span> — {sk.reason}
                        </p>
                        {sk.pros.length > 0 && (
                          <p className="mt-1 text-xs text-muted break-words">
                            {sk.pros.join(', ')}{sk.count > sk.pros.length ? ` … +${sk.count - sk.pros.length} more` : ''}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )
              )}
            </div>

            <div className="px-5 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between gap-3">
              <div className="text-xs text-muted">
                {recalc.result && !recalc.result.applied && recalc.result.updates.length > 0 && !recalc.result.apply_enabled && (
                  <>Preview only — V1 still owns per diem updates (processing is disabled server-side).</>
                )}
              </div>
              <div className="flex items-center gap-3">
                <button onClick={closeRecalc} disabled={recalc.applying} className={btnSecondary}>Close</button>
                {recalc.result && !recalc.result.applied && recalc.result.updates.length > 0 && (
                  <button onClick={confirmRecalc} disabled={recalc.applying || !recalc.result.apply_enabled}
                    className="px-4 py-2 text-sm font-medium text-accent-fg bg-accent rounded-lg hover:bg-accent-hover disabled:opacity-50 inline-flex items-center gap-2">
                    {recalc.applying
                      ? (<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Processing…</>)
                      : (<><CheckIcon className="w-4 h-4" /> Process {recalc.result.updates.length} Update{recalc.result.updates.length === 1 ? '' : 's'}</>)}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Unsaved-changes confirm (shown when a close was vetoed while dirty). */}
      {confirmDiscard && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-gray-900/40">
          <div
            ref={discardPanelRef}
            style={discardPanelStyle}
            className="w-96 max-w-[90vw] rounded-xl border border-gray-200 dark:border-gray-700 bg-panel shadow-xl"
          >
            <ModalHeader
              title="Discard unsaved changes?"
              onClose={() => setConfirmDiscard(false)}
              dragHandleProps={discardDragHandleProps}
              className="rounded-t-xl"
            />
            <div className="p-5">
              <p className="text-sm text-muted">
                {isNew ? 'The rules entered in this drawer have not been created yet.' : 'Your edits to these rules have not been saved.'}
              </p>
              <div className="mt-4 flex justify-end gap-3">
                <button onClick={() => setConfirmDiscard(false)} className={btnSecondary}>Keep Editing</button>
                <button onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 dark:bg-red-500 rounded-lg hover:bg-red-700 dark:hover:bg-red-600">
                  Discard
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Drawer>
  );
};

export default CustomerPerdiemEditDrawer;
